import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedFetch } from './useAuth'

export type OrganizationAccessRole = 'viewer' | 'editor' | 'moderator'
export interface OrganizationAccessPolicy {
  orgAccessMode: 'none' | 'org'
  orgAccessRole: OrganizationAccessRole
}

export function useDiagramAccessPolicy(diagramId?: string) {
  const fetcher = useAuthenticatedFetch()
  const queryClient = useQueryClient()

  const policy = useQuery({
    queryKey: ['diagram', diagramId, 'access-policy'],
    enabled: Boolean(diagramId),
    // The PATCH below invalidates this key and policy changes also arrive via
    // the invalidation feed, so remounts can trust a fresh-enough cache.
    staleTime: 60_000,
    queryFn: async (): Promise<OrganizationAccessPolicy> => {
      const response = await fetcher(`/api/diagrams/${diagramId}/access-policy`)
      if (!response.ok) throw new Error('Failed to load diagram access policy')
      return (await response.json()).policy as OrganizationAccessPolicy
    },
  })

  const updatePolicy = useMutation({
    mutationFn: async (patch: Partial<OrganizationAccessPolicy>): Promise<OrganizationAccessPolicy> => {
      const response = await fetcher(`/api/diagrams/${diagramId}/access-policy`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (!response.ok) throw new Error('Failed to update diagram access policy')
      return (await response.json()).policy as OrganizationAccessPolicy
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['diagram', diagramId, 'access-policy'], next)
      queryClient.invalidateQueries({ queryKey: ['diagram', diagramId, 'access'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  return { policy, updatePolicy }
}
